
import { debounce, get } from "lodash";
import axios, { CancelTokenSource, CancelToken } from 'axios';
import { namespace, State, Getter } from "vuex-class";
import { getDebouncePeriod } from '@/utils/debounce-helper';
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";

import { validationMixin } from "vuelidate";
import MapSetupMixin from "@/mixins/MapSetupMixin";
import { maxLength, integer, required } from "vuelidate/lib/validators";
import { MarkerIcons } from "@sureview/v2-mapping-saas"
import vselect3 from "vselect3"
import api from "@/services/api.service";
import { MapLayerItemType, MapLayerItem, MapLayer, MapLayerItemDeviceOption } from "@/store/map-layers/types";
import { FeaturesList } from "@/store/types";
import ElevationSelect from "@/components/form/ElevationSelect.vue"

const MapLayers = namespace("mapLayers");

interface IMapLayerDisplay {
	title: string;
	mapLayerId: number;
}

@Component({
	components: {
		vselect3,
		"elevation-select": ElevationSelect
	},
	validations: {
		updatedMapLayerItem: {
			title: {
				required,
				maxLength: maxLength(250)
			},
			minElevation: {
				integer
			},
			maxElevation: {
				integer
			}
		}
	}
})

export default class MapLayerItemSetup extends Mixins(validationMixin, MapSetupMixin) {
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@Prop({ default: () => [] }) public mapLayersInBounds: MapLayer[]; // The Item we are Creating / Editing
	@Prop(Number) public groupId: number;
	@Prop(Number) public currentFloor: number;

	@State private featuresList: FeaturesList;

	@MapLayers.State("mapItemIcons") private mapIcons!: MarkerIcons;
	@MapLayers.Getter("getMapLayerItemTypes") public mapItemTypes: MapLayerItemType[];

	private debounceCancelToken: CancelTokenSource;
	private debouncePeriod: number = getDebouncePeriod();

	private showElevationError: boolean = false;
	private isDeleteConfirmationVisible: boolean = false;

	private mapLayerDisplay: IMapLayerDisplay = { title: "", mapLayerId: 0 };
	private mapItemTemplate: MapLayerItem; // The thing we validate against and reset to

	private selectedFloor: number = 1;
	private selectedLayers: MapLayer[] = null;
	private selectedDevice: MapLayerItemDeviceOption = null;

	public async mounted() {
		// create our local mapItem object,
		if (this.mapLayerItem) {
			this.updatedMapLayerItem = { ...this.mapLayerItem };

			this.selectedFloor = this.isMapSetupUseElevationLabels ?
				this.updatedMapLayerItem.minElevation :
				this.updatedMapLayerItem.minElevation + 1;

			//Ensure mapLayerIds is initialized
			if (!this.updatedMapLayerItem.mapLayerIds) {
				this.updatedMapLayerItem.mapLayerIds = [];
			}

			if (this.updatedMapLayerItem.mapLayerItemId == 0) {
				// Create default values for the values we don't receive if we are creating an object
				this.updatedMapLayerItem.mapLayerItemId = 0;
				this.updatedMapLayerItem.title = "";
				this.updatedMapLayerItem.extraValue = "";
				this.updatedMapLayerItem.regionPath = "";
				this.updatedMapLayerItem.objectId = 0;
				this.updatedMapLayerItem.mapLayerIds = [];
				this.toggleEdit();
			}
		}

		await this.callLoadDevices();

		// Initialize the template the fields will reset to on reset
		this.mapItemTemplate = Object.assign({}, this.updatedMapLayerItem);

		// setup for select dropdowns
		this.setDefaultDevice();
		this.setDefaultLayer();

		// call layer selected to set layer title for display
		this.setLayerDisplay();
	}

	@Watch("selectedFloor")
    private onSelectedFloorUpdated() {
        if (this.isMapSetupUseElevationLabels) {
            if(this.isMapSetupElevationsEnabled)
            {
                this.mapLayerItem.minElevation = this.updatedMapLayerItem.minElevation;
                this.mapLayerItem.maxElevation = this.updatedMapLayerItem.maxElevation;
            }
            else
            {
                this.mapLayerItem.minElevation = this.selectedFloor;
                this.mapLayerItem.maxElevation = this.selectedFloor;
				this.updatedMapLayerItem.minElevation = this.selectedFloor;
            	this.updatedMapLayerItem.maxElevation = this.selectedFloor;
            }
        }
        else
        {
            this.mapLayerItem.minElevation = this.selectedFloor - 1;
            this.mapLayerItem.maxElevation = this.selectedFloor - 1;
			this.updatedMapLayerItem.minElevation = this.selectedFloor - 1;
            this.updatedMapLayerItem.maxElevation = this.selectedFloor - 1;
        }
    }

	private get isMapSetupElevationsEnabled(): boolean {
		return get(this.featuresList, ["MapSetup", "Elevations"]);
	}

	private get isMapSetupUseElevationLabels(): boolean {
		return get(this.featuresList, ["MapSetup", "ElevationLabels"]);
	}

	private get hidePlottedEnabled(): boolean {
		return this.getFeature(["MapSetup", "HidePlotted"]);
	}

	private get getDefaultValueForElevationOptions(): number {
		if (!this.isEditMode) {
			return null;
		} else{
			return this.currentFloor;
		}
	}

	private setDefaultDevice() {
		if (this.updatedMapLayerItem.mapLayerItemId === 0) {
			return;
		}
		this.selectedDevice = new MapLayerItemDeviceOption(this.updatedMapLayerItem, 0);
	}

	/**
	 * Sets the initial selected layer
	 */
	private setDefaultLayer() {
		if (this.updatedMapLayerItem.mapLayerIds.length === 0) {
			return;
		}
		this.selectedLayers = this.mapLayersInBounds.filter((layer: MapLayer) => this.updatedMapLayerItem.mapLayerIds.includes(layer.mapLayerId));
	}

	/**
	 * Sets the layer display object for the UI
	 */
	private setLayerDisplay() {
		// If there are no layers display placeholder
		if (this.selectedLayers === null || this.selectedLayers.length === 0) {
			this.mapLayerDisplay = { title: "No Layer Linked", mapLayerId: 0 };
		} else {
			// Set the displayLayer to be the first selection from the selected layers
			this.mapLayerDisplay.title = this.selectedLayers[0].title;
			this.mapLayerDisplay.mapLayerId = this.selectedLayers[0].mapLayerId;
		}
	}

	/**
	 * Sets the selected device to the local map object
	 */
	private deviceSelected() {
		this.updatedMapLayerItem.title = this.selectedDevice?.title || "untitled";
		this.updatedMapLayerItem.objectId = this.selectedDevice?.objectId || 0;
	}

	/**
	 * Open the delete confimation dialog
	 */
	private deleteMapLayerItemDialog() {
		this.isDeleteConfirmationVisible = true;
	}

	/**
	 * Toggles the popup edit mode
	 */
	private toggleEdit() {
		this.isEditMode = !this.isEditMode;
	}

	@Watch("updatedMapLayerItem.maxElevation")
	@Watch("updatedMapLayerItem.minElevation")
	private isValidElevation(): void {
		try {
			const min = parseInt(this.updatedMapLayerItem.minElevation.toString(), 10);
			const max = parseInt(this.updatedMapLayerItem.maxElevation.toString(), 10);

			if(!isNaN(min) && isNaN(max)) {
				this.showElevationError = true;
				return;
			}

			if(isNaN(min) || isNaN(max)) {
				// Error should not show, however the submit button will still be disabled.
				this.showElevationError = false;
				return;
			}

			this.showElevationError = min > max;
		}
		catch (e) {
			this.updatedMapLayerItem.minElevation = 0;
			this.updatedMapLayerItem.maxElevation = 0;
			console.error('Error parsing max/min elevation, resetting values to 0', e);
		}
	}

	private async saveMapItemDetails() {
		if (this.selectedLayers) {
			this.updatedMapLayerItem.mapLayerIds = this.selectedLayers.map(l => l.mapLayerId);
		}
		try {
			await api.putMapLayerItem(this.updatedMapLayerItem);
			this.mapItemTemplate = Object.assign({}, this.updatedMapLayerItem);
			this.$emit("mapLayerItemSaved");
		} catch (e) {
			const errorTitle = "Error Saving Map Marker";
			if (e.response && e.response.data) {
				switch (e.response.status) {
					case 403:
						this.$notify({
							type: "error",
							title: errorTitle,
							text: `You do not have permissions to save this map marker`
						});
						break;
					case 500:
						this.$notify({
							type: "error",
							title: errorTitle,
							text: "Unexpected error saving map marker. Please contact support if this problem persists."
						});
						break;
				}
			}
			console.error(e);
		}
	}

	/**
	 * Reset the UI to its default state
	 */
	private resetMapItemData() {
		// If we are creating an item close the popup on reset
		if (this.updatedMapLayerItem.mapLayerItemId == 0) {
			this.$emit("mapLayerItemWindowClosed");
		}
		this.updatedMapLayerItem = Object.assign({}, this.mapItemTemplate);

		this.setDefaultDevice();
		this.setDefaultLayer();
		this.setLayerDisplay();
		this.toggleEdit();
	}

	/**
	 * Delete the local item from the db then close the delete dialog and the popup
	 */
	private async deleteMapLayerItemDelegate() {
		this.isDeleteConfirmationVisible = false;
		try {
			await api.deleteMapLayerItem(this.updatedMapLayerItem.mapLayerItemId);
			this.$emit("mapLayerItemDeleted", { mapLayerItemId: this.updatedMapLayerItem.mapLayerItemId });
		} catch (e) {
			const errorTitle = "Error Deleting Map Marker";
			if (e.response &&
				e.response.data) {
				switch(e.response.status) {
					case 403:
						this.$notify({
							type: "error",
							title: errorTitle,
							text: `You do not have permissions to delete this map marker`
						});
						break;
					case 500:
						this.$notify({
							type: "error",
							title: errorTitle,
							text: "Unexpected error deleting map marker. Please contact support if this problem persists."
						});
						break;
				}
			}
			console.error(e);
		}
	}

	private get getDeviceTypeArticle(): string {
		switch(this.updatedMapLayerItem.mapLayerItemTypeId) {
			case 2:
			case 4:
			case 5:
				return "an";
			default:
				return "a"
		}
	}

	private get getDeviceSelectPlaceholder(): string {
		return `Please select ${this.getDeviceTypeArticle} ${this.typeDictionary.get(this.updatedMapLayerItem.mapLayerItemTypeId)} device`;
	}

	private get getHidePlottedLabel(): string {
		return `Exclude already plotted ${this.typeDictionaryPluralized.get(this.updatedMapLayerItem.mapLayerItemTypeId)}`;
	}

	/**
	 * @summary Debounce method to handle our waiting time when user is inputting data.
	 * @param query Data that the user has inserted.
	 */
	private debounceFilterQuery: (query: string) => Promise<void> = debounce(async (query: string) => {
		if(this.debounceCancelToken) {
			this.debounceCancelToken.cancel();
		}

		this.debounceCancelToken = axios.CancelToken.source();

		try {
			await this.callLoadDevices(query, this.debounceCancelToken.token);
		}
		catch (e) {
			return;
		}

	}, this.debouncePeriod);

	/**
     * @summary Used to call loadDevices from MapSetupMixin. Required for debounce to work.
	 * @param filter The filter to be used for the device search.
     */
	private async callLoadDevices(filter: string = null, cancelToken: CancelToken = null): Promise<void> {
		await this.loadDevices({filter: filter, groupId: this.groupId, cancelToken: cancelToken});
	}
}
